// Ações puras do caça-palavras.
//
// Regra do shell: quando uma ação não muda nada, devolver a MESMA referência do
// doc — é assim que o dirty tracking e o history sabem que nada aconteceu.

import { cellIndex, lineCells, normalizeSolution, type WordEntry, type WordsearchDoc } from './doc';

export type WordsearchAction =
  | { type: 'SET_TITULO'; titulo: string }
  | { type: 'PLACE_WORD'; ref: string; solution: string; x1: number; y1: number; x2: number; y2: number }
  | { type: 'REMOVE_WORD'; ref: string }
  | { type: 'SET_CLUE'; ref: string; clue: string }
  | { type: 'TYPE_LETTER'; x: number; y: number; letter: string }
  | { type: 'FILL_RANDOM' };

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

function inBounds(doc: WordsearchDoc, x: number, y: number): boolean {
  return x >= 1 && y >= 1 && x <= doc.width && y <= doc.height;
}

function placeWord(doc: WordsearchDoc, action: Extract<WordsearchAction, { type: 'PLACE_WORD' }>) {
  const solution = normalizeSolution(action.solution);
  if (solution.length === 0) return doc;
  if (doc.words.some((w) => w.ref === action.ref)) return doc; // ref já usado

  const draft: WordEntry = {
    ref: action.ref,
    solution,
    clue: '',
    x1: action.x1,
    y1: action.y1,
    x2: action.x2,
    y2: action.y2,
  };

  const cells = lineCells(draft);
  if (cells.length === 0) return doc; // nem H, nem V, nem diagonal
  if (cells.length !== solution.length) return doc; // reta e palavra de tamanhos diferentes
  if (!cells.every((c) => inBounds(doc, c.x, c.y))) return doc;

  const letters = [...doc.letters];
  cells.forEach((c, i) => {
    letters[cellIndex(c.x, c.y, doc.width)] = solution[i];
  });

  return { ...doc, letters, words: [...doc.words, draft] };
}

export function wordsearchReducer(doc: WordsearchDoc, action: WordsearchAction): WordsearchDoc {
  switch (action.type) {
    case 'SET_TITULO':
      return action.titulo === doc.titulo ? doc : { ...doc, titulo: action.titulo };

    case 'PLACE_WORD':
      return placeWord(doc, action);

    case 'REMOVE_WORD': {
      const words = doc.words.filter((w) => w.ref !== action.ref);
      // As letras FICAM: viram enchimento. Apagá-las quebraria palavras que cruzam.
      return words.length === doc.words.length ? doc : { ...doc, words };
    }

    case 'SET_CLUE': {
      const i = doc.words.findIndex((w) => w.ref === action.ref);
      if (i === -1 || doc.words[i].clue === action.clue) return doc;
      const words = [...doc.words];
      words[i] = { ...words[i], clue: action.clue };
      return { ...doc, words };
    }

    case 'TYPE_LETTER': {
      if (!inBounds(doc, action.x, action.y)) return doc;
      const letter = action.letter === '' ? '' : normalizeSolution(action.letter).slice(0, 1);
      const idx = cellIndex(action.x, action.y, doc.width);
      if (doc.letters[idx] === letter) return doc;
      const letters = [...doc.letters];
      letters[idx] = letter;
      return { ...doc, letters };
    }

    case 'FILL_RANDOM': {
      if (!doc.letters.some((l) => l === '')) return doc;
      const letters = doc.letters.map((l) =>
        l === '' ? ALPHABET[Math.floor(Math.random() * ALPHABET.length)] : l,
      );
      return { ...doc, letters };
    }

    default:
      return doc;
  }
}
